import type { Manifest, ExtensionLogger } from '@sap/ux-specification-types';
import type { ConvertedMetadata, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import type { SapUiAppPageV4 } from '@sap/ux-specification-types/src/v4/webapp/manifest/sapUiPageV4';
import { ManifestSection, UIVOCABULARY, LogSeverity, MANIFESTPATH } from '@sap/ux-specification-types';
import { log } from '../../../extensionLogger';
import i18next from 'i18next';
import type { AppContext } from '../../common';

/**
 * Interface representing a Fiori application's core metadata.
 * Contains manifest, annotation vocabulary tools (AVT), and page configurations.
 */
export interface AppInterface {
    manifest: Manifest;
    avt: ConvertedMetadata;
    pages: SapUiAppPageV4[];
    alias?: string;
}

/**
 * Generic App class that implements the App interface.
 * Contains general, reusable methods applicable to any Fiori application.
 * Provides utilities for accessing manifest data, entity information, page discovery, and more.
 * Automatically extracts pages from the manifest during construction.
 */
export class App implements AppInterface {
    manifest: Manifest;
    avt: ConvertedMetadata;
    pages: SapUiAppPageV4[];
    alias?: string;
    private minUI5Version?: string | string[];
    logger?: ExtensionLogger;

    /**
     * Configuration object for decorator context.
     * Provides access to app-level configuration via `app.*` path builders.
     */
    config: AppContext;

    /**
     * Constructor for the App class.
     *
     * @param manifest The application manifest
     * @param avt The converted metadata from annotation vocabulary tools
     * @param logger Optional logger for error messages
     */
    constructor(manifest: Manifest, avt: ConvertedMetadata, logger?: ExtensionLogger) {
        this.manifest = manifest;
        this.avt = avt;
        this.logger = logger;
        this.alias = this.getUIVocabularyAlias();
        this.setMinUI5Version();
        this.extractPagesFromManifest();

        // Initialize config for decorator context
        this.config = {
            manifest: this.manifest,
            minUI5Version: this.getMinUI5Version()
        };
    }

    /**
     * Extracts pages from the manifest's routing targets.
     * Called automatically during construction.
     */
    private extractPagesFromManifest(): void {
        const pages = this.manifest?.[ManifestSection.ui5]?.routing?.targets as unknown as SapUiAppPageV4[];
        // Validate pages and log error if no pages found
        if (!pages || pages.length === 0) {
            if (this.logger) {
                log(this.logger, {
                    severity: LogSeverity.Info,
                    message: i18next.t('NOPAGES', { appId: this.getAppId() }),
                    location: {
                        path: MANIFESTPATH,
                        range: [ManifestSection.ui5, 'routing']
                    }
                });
            }
        }
        this.pages = pages;
    }

    // ==================== UI5 Version Management ====================

    /**
     * Sets the minimum UI5 version.
     */
    private setMinUI5Version(): void {
        this.minUI5Version = this.manifest?.['sap.ui5']?.dependencies?.minUI5Version;
    }

    /**
     * Gets the minimum UI5 version as a string.
     * If minUI5Version is an array, returns the first element.
     *
     * @todo Handle array case properly if needed.
     * @returns The minimum UI5 version as a string, or undefined if not set
     */
    getMinUI5Version(): string {
        return typeof this.minUI5Version === 'string' ? this.minUI5Version : this.minUI5Version?.[0];
    }

    // ==================== Manifest Access ====================

    /**
     * Gets the manifest.
     *
     * @returns The application manifest
     */
    getManifest(): Manifest {
        return this.manifest;
    }

    /**
     * Gets the application ID from the manifest.
     *
     * @returns The application ID, or undefined if not found
     */
    getAppId(): string | undefined {
        return this.manifest?.['sap.app']?.id;
    }

    /**
     * Gets the application version from the manifest.
     *
     * @returns The application version, or undefined if not found
     */
    getAppVersion(): string | undefined {
        return this.manifest?.['sap.app']?.applicationVersion?.version;
    }

    /**
     * Gets the application title from the manifest.
     *
     * @returns The application title, or undefined if not found
     */
    getAppTitle(): string | undefined {
        return this.manifest?.['sap.app']?.title;
    }

    // ==================== AVT (Annotation Vocabulary Tools) Access ====================

    /**
     * Gets the converted metadata (AVT).
     *
     * @returns The converted metadata containing entity types, entity sets, and annotations
     */
    getAVT(): ConvertedMetadata {
        return this.avt;
    }

    /**
     * Gets a specific entity set by name.
     *
     * @param name - The name of the entity set to find
     * @returns The entity set, or undefined if not found
     */
    getEntitySet(name: string): EntitySet | undefined {
        return this.avt?.entitySets?.find((es) => es.name === name);
    }

    /**
     * Gets the entity type for a given entity set.
     *
     * @param entitySetName - The name of the entity set
     * @returns The entity type, or undefined if not found
     */
    getEntityType(entitySetName: string): EntityType | undefined {
        const entitySet = this.getEntitySet(entitySetName);
        return entitySet?.entityType;
    }

    /**
     * Finds the annotation alias for a given vocabulary namespace.
     *
     * @param vocabulary - The vocabulary namespace to search for (e.g., 'com.sap.vocabularies.UI.v1')
     * @returns The alias for the vocabulary, or undefined if not found
     */
    private findAnnotationAlias(vocabulary: string): string | undefined {
        if (!this.avt?.references) {
            return undefined;
        }
        let aliasReference = this.avt.references.find((reference) => {
            return reference.namespace === vocabulary;
        });
        if (!aliasReference) {
            aliasReference = this.avt.references.find((reference) => {
                return reference.alias === vocabulary;
            });
        }
        if (!aliasReference) {
            aliasReference = this.avt.references.find((reference) => {
                return (
                    reference.alias.toUpperCase() ===
                    vocabulary.split('SAP__')[vocabulary.split('SAP__').length - 1].toUpperCase()
                );
            });
        }
        return aliasReference?.alias;
    }

    /**
     * Gets the alias for the UI vocabulary (com.sap.vocabularies.UI.v1).
     * This is a convenience method for the commonly-used UI vocabulary.
     *
     * @param vocabulary - The vocabulary namespace to search for (default is UIVOCABULARY)
     * @returns The alias for the UI vocabulary, or undefined if not found
     */
    getUIVocabularyAlias(vocabulary = UIVOCABULARY): string | undefined {
        return this.findAnnotationAlias(vocabulary);
    }

    // ==================== Page Management ====================

    /**
     * Gets all pages.
     *
     * @returns Array of page configurations
     */
    getPages(): SapUiAppPageV4[] {
        return this.pages;
    }

    /**
     * Checks if the application has any pages configured.
     *
     * @returns True if pages exist, false otherwise
     */
    hasPages(): boolean {
        return this.pages && this.pages.length > 0;
    }
}

/**
 * Factory function to create an App instance.
 * Provides a convenient way to instantiate the App class.
 * Pages are automatically extracted from the manifest.
 *
 * @param manifest - The application manifest
 * @param avt - The converted metadata from annotation vocabulary tools
 * @param logger - Optional logger for error messages
 * @returns A new App instance with pages automatically extracted
 */
export function createApp(manifest: Manifest, avt: ConvertedMetadata, logger?: ExtensionLogger): App {
    return new App(manifest, avt, logger);
}
